import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';
import { Modal } from '@spirit-testing/component-library';
import { pairwise, startWith, Subscription } from 'rxjs';
import { CreateTestCaseLinkRequest, TestCaseLinkType } from '../../../models/test-case-link.model';
import { Requirement } from '../../../models/requirement.model';
import { RequirementsService } from '../../../services/requirements.service';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';

// Backend-@Size: reference ist auf 1024 Zeichen begrenzt.
const REFERENCE_MAX = 1024;

const LINK_TYPES: readonly TestCaseLinkType[] = ['JIRA', 'GITHUB', 'TESTCASE', 'REQUIREMENT', 'URL'];

interface LinkTypeMeta {
  icon: string;
  color: string;
}

// Farben aus dem Design-Bundle (NEX-827, Ticket-verbindlich); wo ein Projekt-Token exakt
// passt, wird dieser verwendet (Konsistenz mit dem restlichen Code).
const LINK_TYPE_META: Record<TestCaseLinkType, LinkTypeMeta> = {
  JIRA: { icon: 'bug_report', color: '#2563eb' },
  GITHUB: { icon: 'code', color: 'var(--to-text-strong)' },
  TESTCASE: { icon: 'checklist', color: 'var(--to-brand)' },
  REQUIREMENT: { icon: 'assignment', color: 'var(--to-brand-text)' },
  URL: { icon: 'link', color: 'var(--to-text-muted)' },
};

type KeyedRequirement = Requirement & { key: string };

function hasKey(requirement: Requirement): requirement is KeyedRequirement {
  return !!requirement.key;
}

function byKey(a: KeyedRequirement, b: KeyedRequirement): number {
  return a.key.localeCompare(b.key, undefined, { numeric: true });
}

function urlSchemeValidator(group: AbstractControl): ValidationErrors | null {
  const type = group.get('type')?.value as TestCaseLinkType;
  const reference = (group.get('reference')?.value as string) ?? '';
  if (type !== 'URL') {
    return null;
  }
  const normalized = reference.trim().toLowerCase();
  if (normalized && !normalized.startsWith('http://') && !normalized.startsWith('https://')) {
    return { urlScheme: true };
  }
  return null;
}

@Component({
  selector: 'app-add-link-modal',
  imports: [ReactiveFormsModule, TranslatePipe, Modal, MatIcon, MatAutocomplete, MatAutocompleteTrigger, MatOption],
  templateUrl: './add-link-modal.component.html',
  styleUrl: './add-link-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddLinkModalComponent implements AfterViewInit {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly requirementsService = inject(RequirementsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly visible = model(false);
  readonly added = output<CreateTestCaseLinkRequest>();

  readonly linkTypes = LINK_TYPES;
  readonly fieldLengths = FIELD_LENGTHS;
  readonly referenceMax = REFERENCE_MAX;

  readonly form = this.fb.group(
    {
      type: this.fb.control<TestCaseLinkType>('JIRA', Validators.required),
      reference: ['', [Validators.required, Validators.maxLength(REFERENCE_MAX)]],
      displayLabel: ['', Validators.maxLength(FIELD_LENGTHS.NAME_MAX)],
    },
    { validators: urlSchemeValidator },
  );

  readonly typeDropdownOpen = signal(false);
  readonly activeTypeIndex = signal(0);
  readonly linkType = signal<TestCaseLinkType>('JIRA');

  // null = für diese Öffnung des Dialogs noch nicht geladen
  readonly requirements = signal<KeyedRequirement[] | null>(null);
  readonly requirementsLoading = signal(false);
  readonly requirementsLoadError = signal(false);
  readonly requirementSearch = signal('');
  private readonly requirementSelected = signal(false);
  private requirementsSubscription: Subscription | null = null;

  readonly requirementsEmpty = computed(() => this.requirements()?.length === 0);
  readonly requirementPickerDisabled = computed(
    () => this.requirementsLoading() || this.requirementsLoadError() || this.requirementsEmpty(),
  );
  readonly filteredRequirements = computed(() => {
    const all = this.requirements() ?? [];
    const term = this.requirementSearch().trim().toLowerCase();
    if (!term || this.requirementSelected()) {
      return all;
    }
    return all.filter((r) => r.key.toLowerCase().includes(term) || r.title.toLowerCase().includes(term));
  });

  readonly requirementOptionLabel = (requirement: KeyedRequirement | null): string =>
    requirement ? `${requirement.key} — ${requirement.title}` : '';

  @ViewChild('content') contentTemplate!: TemplateRef<unknown>;
  resolvedTemplate: TemplateRef<unknown> | null = null;

  constructor() {
    effect(() => {
      if (!this.visible()) {
        this.typeDropdownOpen.set(false);
      }
    });
    const typeControl = this.form.controls.type;
    typeControl.valueChanges
      .pipe(startWith(typeControl.value), pairwise(), takeUntilDestroyed())
      .subscribe(([previous, next]) => this.onTypeChanged(previous, next));
  }

  ngAfterViewInit(): void {
    this.resolvedTemplate = this.contentTemplate;
    this.cdr.detectChanges();
  }

  hasUrlSchemeError(): boolean {
    return this.form.hasError('urlScheme');
  }

  referenceErrorKey(): string | null {
    const control = this.form.controls.reference;
    if (control.touched && control.hasError('required')) {
      return this.linkType() === 'REQUIREMENT'
        ? 'testCases.form.links.modal.requirementRequired'
        : 'testCases.form.links.modal.referenceRequired';
    }
    if (this.hasUrlSchemeError()) {
      return 'testCases.form.links.modal.urlInvalid';
    }
    return null;
  }

  typeMeta(type: TestCaseLinkType): LinkTypeMeta {
    return LINK_TYPE_META[type];
  }

  selectedTypeMeta(): LinkTypeMeta {
    return this.typeMeta(this.form.controls.type.value);
  }

  toggleTypeDropdown(): void {
    if (this.typeDropdownOpen()) {
      this.closeTypeDropdown();
    } else {
      this.openTypeDropdown();
    }
  }

  openTypeDropdown(): void {
    const idx = this.linkTypes.indexOf(this.form.controls.type.value);
    this.activeTypeIndex.set(idx >= 0 ? idx : 0);
    this.typeDropdownOpen.set(true);
  }

  closeTypeDropdown(): void {
    this.typeDropdownOpen.set(false);
  }

  selectType(type: TestCaseLinkType): void {
    this.form.controls.type.setValue(type);
    this.closeTypeDropdown();
  }

  setActiveType(type: TestCaseLinkType): void {
    const idx = this.linkTypes.indexOf(type);
    if (idx >= 0) {
      this.activeTypeIndex.set(idx);
    }
  }

  isActiveType(type: TestCaseLinkType): boolean {
    return this.linkTypes[this.activeTypeIndex()] === type;
  }

  typeOptionId(type: TestCaseLinkType): string {
    return `add-link-type-option-${type}`;
  }

  activeTypeOptionId(): string {
    return this.typeOptionId(this.linkTypes[this.activeTypeIndex()]);
  }

  onTypeTriggerKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.typeDropdownOpen()) {
          this.openTypeDropdown();
        } else {
          this.moveActiveType(1);
        }
        return;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.typeDropdownOpen()) {
          this.openTypeDropdown();
        } else {
          this.moveActiveType(-1);
        }
        return;
      case 'Home':
        if (this.typeDropdownOpen()) {
          event.preventDefault();
          this.activeTypeIndex.set(0);
        }
        return;
      case 'End':
        if (this.typeDropdownOpen()) {
          event.preventDefault();
          this.activeTypeIndex.set(this.linkTypes.length - 1);
        }
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!this.typeDropdownOpen()) {
          this.openTypeDropdown();
        } else {
          this.selectType(this.linkTypes[this.activeTypeIndex()]);
        }
        return;
      case 'Escape':
        if (this.typeDropdownOpen()) {
          event.preventDefault();
          event.stopPropagation();
          this.closeTypeDropdown();
        }
        return;
      case 'Tab':
        this.closeTypeDropdown();
        return;
    }
  }

  loadRequirements(): void {
    this.requirementsSubscription?.unsubscribe();
    this.requirementsLoading.set(true);
    this.requirementsLoadError.set(false);
    this.requirementsSubscription = this.requirementsService
      .list(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (requirements) => {
          this.requirements.set(requirements.filter(hasKey).sort(byKey));
          this.requirementsLoading.set(false);
        },
        error: () => {
          this.requirementsLoadError.set(true);
          this.requirementsLoading.set(false);
        },
      });
  }

  onRequirementSearch(event: Event): void {
    this.requirementSearch.set((event.target as HTMLInputElement).value);
    this.requirementSelected.set(false);
    if (this.form.controls.reference.value) {
      this.form.controls.reference.setValue('');
    }
  }

  onRequirementSelected(event: MatAutocompleteSelectedEvent): void {
    const requirement = event.option.value as KeyedRequirement;
    this.requirementSearch.set(this.requirementOptionLabel(requirement));
    this.requirementSelected.set(true);
    this.form.controls.reference.setValue(requirement.key);
    this.markReferenceTouched();
  }

  // Ein Klick auf eine Option nimmt dem Feld den Fokus, bevor die Auswahl ankommt; bei offener
  // Liste übernimmt daher erst das Schließen der Liste das "touched".
  onRequirementBlur(panelOpen: boolean): void {
    if (!panelOpen) {
      this.markReferenceTouched();
    }
  }

  markReferenceTouched(): void {
    this.form.controls.reference.markAsTouched();
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const label = raw.displayLabel.trim();
    this.added.emit({
      type: raw.type,
      reference: raw.reference.trim(),
      displayLabel: label || null,
    });
    this.close();
  }

  onCancel(): void {
    this.close();
  }

  private moveActiveType(delta: number): void {
    const len = this.linkTypes.length;
    this.activeTypeIndex.update((i) => (i + delta + len) % len);
  }

  private onTypeChanged(previous: TestCaseLinkType, next: TestCaseLinkType): void {
    this.linkType.set(next);
    if (previous === next) {
      return;
    }
    if (previous === 'REQUIREMENT' || next === 'REQUIREMENT') {
      this.form.controls.reference.reset('');
      this.requirementSearch.set('');
      this.requirementSelected.set(false);
    }
    if (next === 'REQUIREMENT' && this.requirements() === null && !this.requirementsLoading()) {
      this.loadRequirements();
    }
  }

  private close(): void {
    this.visible.set(false);
    this.form.reset({ type: 'JIRA', reference: '', displayLabel: '' });
    this.closeTypeDropdown();
    this.requirementsSubscription?.unsubscribe();
    this.requirementsSubscription = null;
    this.requirements.set(null);
    this.requirementsLoading.set(false);
    this.requirementsLoadError.set(false);
  }
}
